import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminUserListItem, UserRole } from '@proref/types';
import { PageHeader } from '../../components/shared/PageHeader';
import { RouteTabs } from '../../components/shared/RouteTabs';
import { Icon } from '../../components/shared/Icons';
import { Input } from '../../components/shared/Input';
import { Avatar } from '../../components/shared/Avatar';
import { ListSkeleton } from '../../components/shared/Skeleton';
import { DataPagination } from '../../components/shared/DataPagination';
import { usePagination } from '../../hooks/use-pagination';
import { useUsersQuery } from '../../hooks/use-users';
import { EditUserDialog } from '../../components/admin/EditUserDialog';
import { RemoveUserDialog } from '../../components/admin/RemoveUserDialog';
import { useAuth } from '../../contexts/auth-context';
import { apiErrorMessage } from '../../lib/api';
import { roleMeta } from './UsersPage';

const USERS_PAGE_SIZE = 15;

// Native select styled to match the app's inputs (mirrors UsersPage's FilterSelect).
function FilterSelect(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className="h-10 rounded-sm border border-line-2 bg-surface px-3 text-sm text-ink outline-none focus:border-ink-3"
      {...props}
    />
  );
}

// The roster tab of the Users page (route: /invites/users): the federation's existing referees +
// admins, with a Remove action — the counterpart to the Invite tab's "add" flow. federation_admin sees
// only their own federation (the API scopes it from the JWT); super_admin reaching this route
// directly sees the whole platform, same as /users, since it has no federation of its own to scope to.
export function FederationUsersPage() {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const { data, isLoading, isError, error } = useUsersQuery();
  const users = useMemo(() => data ?? [], [data]);

  const [search, setSearch] = useState('');
  const [role, setRole] = useState<'all' | UserRole>('all');

  // Filter in memory — one federation's roster is small enough that a client-side filter is
  // instant and avoids a round-trip per keystroke (mirrors UsersPage's platform-wide filter).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (role !== 'all' && u.role !== role) return false;
      if (!q) return true;
      return u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    });
  }, [users, search, role]);

  const { page, setPage, pageCount, pageItems } = usePagination(filtered, USERS_PAGE_SIZE);

  return (
    <>
      <PageHeader
        title={t('admin.users.list.title')}
        sub={t('admin.users.federationRoster.sub')}
      />

      <RouteTabs
        tabs={[
          { to: '/invites', label: t('admin.users.federationRoster.tabInvite'), end: true },
          { to: '/invites/users', label: t('admin.users.federationRoster.tabUsers') },
        ]}
      />

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-[220px] flex-1">
          <Icon.search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin.users.list.searchPlaceholder')}
            className="pl-9"
            aria-label={t('admin.users.list.searchAria')}
          />
        </div>
        <FilterSelect
          value={role}
          onChange={(e) => setRole(e.target.value as 'all' | UserRole)}
          aria-label={t('admin.users.list.filterByRole')}
        >
          <option value="all">{t('admin.users.list.allRoles')}</option>
          <option value="referee">{t('admin.users.list.roleReferees')}</option>
          <option value="federation_admin">{t('admin.users.list.roleFederationAdmins')}</option>
        </FilterSelect>
      </div>

      {isLoading ? (
        <ListSkeleton rows={6} />
      ) : isError ? (
        <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {apiErrorMessage(error, t('admin.users.list.couldNotLoad'))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded border border-dashed border-line-2 bg-bg-2 p-8 text-center">
          <p className="text-sm text-muted">
            {users.length === 0
              ? t('admin.users.federationRoster.noOneYet')
              : t('admin.users.list.noMatches')}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="overflow-x-auto rounded border border-line bg-surface">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-line text-[11px] uppercase tracking-[0.05em] text-muted">
                  <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colUser')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colRole')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colXp')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colStreak')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colJoined')}</th>
                  <th className="px-4 py-2.5 text-right font-semibold">{t('admin.users.federationRoster.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((u: AdminUserListItem) => {
                  const meta = roleMeta(t, u.role);
                  const isSelf = u.id === me?.id;
                  return (
                    <tr key={u.id} className="border-b border-line last:border-0">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <Avatar name={u.full_name} size="sm" />
                          <div className="min-w-0">
                            <div className="truncate font-medium text-ink">
                              {u.full_name}
                              {isSelf && (
                                <span className="ml-1.5 text-[11px] font-normal text-muted">
                                  {t('admin.users.federationRoster.youSuffix')}
                                </span>
                              )}
                            </div>
                            <div className="truncate text-[12px] text-muted">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={
                            'inline-flex h-[22px] items-center rounded-full border px-2.5 text-[11px] font-medium ' +
                            meta.chip
                          }
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-muted">{u.xp}</td>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-muted">{u.current_streak}</td>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-muted">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          {/* Editing yourself is allowed (name + licence); the dialog locks the
                              role/tenancy controls, matching the server's own guard. */}
                          <EditUserDialog user={u} />
                          {/* Can't remove your own account — the server rejects it too, but hiding
                              the control here avoids a confirm-then-fail round trip. */}
                          {!isSelf && <RemoveUserDialog user={u} />}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <DataPagination page={page} pageCount={pageCount} onPageChange={setPage} className="justify-end" />
        </div>
      )}
    </>
  );
}
